function parseJson(value) {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch (_error) {
    return undefined;
  }
}

function parseArray(value) {
  if (!value) {
    return undefined;
  }

  const parsed = parseJson(value);
  if (Array.isArray(parsed)) {
    return parsed;
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return undefined;
}

function readTagElement(element) {
  if (!element) {
    return undefined;
  }

  if (element.tagName === 'SCRIPT') {
    const parsed =
      parseJson(element.textContent || '') ||
      parseJson(element.getAttribute('data-buyer-intent'));
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  }

  const dataJson = parseJson(element.getAttribute('data-buyer-intent'));
  if (dataJson && typeof dataJson === 'object') {
    return dataJson;
  }

  const dataset = element.dataset || {};
  return {
    productIds: parseArray(dataset.productIds),
    categoryIds: parseArray(dataset.categoryIds),
    tag: dataset.tag,
    sourceLocation: dataset.sourceLocation,
    context: parseJson(dataset.context),
  };
}

export function readTags(elements) {
  if (typeof document === 'undefined') {
    return [];
  }

  const found = elements || new Set();
  const legacy = document.getElementById('buyer-intent-tags');
  if (legacy) {
    found.add(legacy);
  }

  // CAPTERRA PATCH: Only scan the full document when no specific elements were
  // provided. When elements are passed (from handleView/handleClick), process
  // only those elements to prevent click element IDs leaking into view events.
  // Upstream fix requested from G2 — remove this patch when SDK is updated.
  if (!elements) {
    document.querySelectorAll('[data-buyer-intent]').forEach((element) => {
      found.add(element);
    });
  }

  return Array.from(found)
    .map((element) => ({ element, tag: readTagElement(element) }))
    .filter((entry) => entry.tag && typeof entry.tag === 'object');
}

export function readElementConfig(element) {
  if (!element || !element.dataset) {
    return {};
  }

  const { origin, userType, distinctId, activityEndpoint } = element.dataset;

  return { origin, userType, distinctId, activityEndpoint };
}

export function resolveEventName(element, fallback) {
  if (element && element.dataset && element.dataset.eventName) {
    return element.dataset.eventName;
  }

  return fallback;
}

function parseIds(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_e) {}
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function readSessionConfig(element) {
  if (!element || typeof element.closest !== 'function') {
    return {};
  }

  const session = element.closest('buyer-intent-session');
  if (!session) {
    return {};
  }

  return {
    origin: session.getAttribute('origin') || undefined,
    activityEndpoint: session.getAttribute('activity-endpoint') || undefined,
    userType: session.getAttribute('user-type') || undefined,
    distinctId: session.getAttribute('distinct-id') || undefined,
  };
}

export function collectSubjectIds(viewElement) {
  if (!viewElement || typeof viewElement.querySelectorAll !== 'function') {
    return { productIds: [], categoryIds: [] };
  }

  const subjects = viewElement.querySelectorAll('buyer-intent-subject');
  const productIds = [];
  const categoryIds = [];

  for (const subject of subjects) {
    const pids = subject.getAttribute('product-ids');
    const pid = subject.getAttribute('product-id');
    if (pids) {
      productIds.push(...parseIds(pids));
    } else if (pid) {
      productIds.push(pid);
    }

    const cids = subject.getAttribute('category-ids');
    const cid = subject.getAttribute('category-id');
    if (cids) {
      categoryIds.push(...parseIds(cids));
    } else if (cid) {
      categoryIds.push(cid);
    }
  }

  return { productIds, categoryIds };
}

export function readViewContext(clickElement) {
  if (!clickElement || typeof clickElement.closest !== 'function') {
    return {};
  }

  const view = clickElement.closest('buyer-intent-view');
  if (!view) {
    return {};
  }

  const viewData = readTagElement(view) || {};
  const subjectIds = collectSubjectIds(view);

  const productIds = [...(viewData.productIds || []), ...subjectIds.productIds];
  const categoryIds = [
    ...(viewData.categoryIds || []),
    ...subjectIds.categoryIds,
  ];

  return {
    ...viewData,
    productIds: productIds.length ? [...new Set(productIds)] : undefined,
    categoryIds: categoryIds.length ? [...new Set(categoryIds)] : undefined,
  };
}
